import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  writeBatch,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { ALIASES_DOC_PATH } from "./schoolAliases";
import { REGISTRATION_COLLECTIONS } from "./registrationsConfig";

const DOC_REF = () => doc(db, ...ALIASES_DOC_PATH);

/**
 * @returns {{
 *   groups: import("./schoolAliases").AliasGroup[],
 *   hiddenSchools: import("./schoolAliases").HiddenSchool[],
 *   schoolContacts: import("./schoolOutreach").SchoolContact[],
 *   manualSchools: import("./schoolOutreach").ManualSchool[],
 * }}
 */
export async function loadAnalyticsConfig() {
  try {
    const snap = await getDoc(DOC_REF());
    if (!snap.exists()) {
      return { groups: [], hiddenSchools: [], schoolContacts: [], manualSchools: [] };
    }
    const data = snap.data();
    return {
      groups: Array.isArray(data.groups) ? data.groups : [],
      hiddenSchools: Array.isArray(data.hiddenSchools) ? data.hiddenSchools : [],
      schoolContacts: Array.isArray(data.schoolContacts) ? data.schoolContacts : [],
      manualSchools: Array.isArray(data.manualSchools) ? data.manualSchools : [],
    };
  } catch (err) {
    console.error("Failed to load analytics config:", err);
    return { groups: [], hiddenSchools: [], schoolContacts: [], manualSchools: [] };
  }
}

export async function loadSchoolAliasGroups() {
  const { groups } = await loadAnalyticsConfig();
  return groups;
}

export async function loadHiddenSchools() {
  const { hiddenSchools } = await loadAnalyticsConfig();
  return hiddenSchools;
}

async function persistAnalyticsConfig(partial) {
  const existing = await loadAnalyticsConfig();
  await setDoc(DOC_REF(), {
    groups: partial.groups ?? existing.groups,
    hiddenSchools: partial.hiddenSchools ?? existing.hiddenSchools,
    schoolContacts: partial.schoolContacts ?? existing.schoolContacts,
    manualSchools: partial.manualSchools ?? existing.manualSchools,
    updatedAt: new Date().toISOString(),
  });
}

export async function saveSchoolAliasGroups(groups) {
  await persistAnalyticsConfig({ groups });
}

export async function saveHiddenSchools(hiddenSchools) {
  await persistAnalyticsConfig({ hiddenSchools });
}

export async function saveSchoolOutreach({ schoolContacts, manualSchools }) {
  await persistAnalyticsConfig({ schoolContacts, manualSchools });
}

/**
 * For each alias group, find every registration document where `schoolName`
 * matches any member label (that differs from the canonical name) and update
 * it to the canonical name.  Uses write batches (max 499 ops each).
 *
 * @param {import("./schoolAliases").AliasGroup[]} groups
 */
export async function renameSchoolRecords(groups) {
  for (const group of groups) {
    const { canonicalName, memberLabels = [] } = group;
    const labelsToRename = memberLabels.filter(
      (l) => l && l.trim() && l.trim() !== canonicalName.trim()
    );
    if (labelsToRename.length === 0) continue;

    for (const colName of REGISTRATION_COLLECTIONS) {
      for (const label of labelsToRename) {
        let snap;
        try {
          snap = await getDocs(
            query(collection(db, colName), where("schoolName", "==", label))
          );
        } catch {
          continue;
        }
        if (!snap || snap.empty) continue;

        let batch = writeBatch(db);
        let count = 0;
        for (const docSnap of snap.docs) {
          batch.update(docSnap.ref, { schoolName: canonicalName });
          count++;
          if (count === 499) {
            await batch.commit();
            batch = writeBatch(db);
            count = 0;
          }
        }
        if (count > 0) await batch.commit();
      }
    }
  }
}
